import {
  MultipleQueriesQuery,
  MultipleQueriesResponse,
} from '@algolia/client-search';
import algoliasearch from 'algoliasearch/lite';

import { GF_ALGOLIA_APP_ID, gfAlgoliaSearchKey } from './algoliaSearchKey';
import { shouldExcludeCurrentUserFromAnalytics } from './searchAnalyticsExclusion';

// NEXT_PUBLIC_ALGOLIA_ID is the GearFocus app (88N6DHABGH) in Production, which
// uses the browser/server key pair (web#1902). Preview and Development point it
// at a separate Algolia app, whose key lives in
// NEXT_PUBLIC_ALGOLIA_PREVIEW_APP_SEARCH_KEY (set in Preview and Development
// only). That variable must never be NEXT_PUBLIC_ALGOLIA_SEARCH_KEY: Next.js
// inlines every NEXT_PUBLIC_ value it sees, even in a branch Production never
// takes, and Production's NEXT_PUBLIC_ALGOLIA_SEARCH_KEY is the old all-index key.
const algoliaAppId = process.env.NEXT_PUBLIC_ALGOLIA_ID ?? '';
const algoliaClient = algoliasearch(
  algoliaAppId,
  algoliaAppId === GF_ALGOLIA_APP_ID
    ? gfAlgoliaSearchKey()
    : process.env.NEXT_PUBLIC_ALGOLIA_PREVIEW_APP_SEARCH_KEY ?? ''
);

// GearMaster Algolia client for master product database
const gearMasterAlgoliaClient = algoliasearch(
  'JW2TPM7CTX',
  process.env.NEXT_PUBLIC_GEARMASTER_ALGOLIA_SEARCH_KEY ?? ''
);

// Helper to create a search client that skips empty queries and excludes
// staff/house-store searches from Algolia analytics (GB #1392).
// NOTE: this only intercepts the batch `search(requests)` entry point used
// by InstantSearch. Callers that go through `initIndex(...).search(...)`
// bypass this wrapper and apply the exclusion themselves (see
// products.service.ts and search.service.ts).
function createSearchClient(client: ReturnType<typeof algoliasearch>) {
  return {
    ...client,
    // Skip empty reads to avoid unnecessary API calls
    search<TObject>(
      requests: readonly MultipleQueriesQuery[]
    ): Readonly<Promise<MultipleQueriesResponse<TObject>>> {
      if (requests.every(({ params }) => !params?.query)) {
        return Promise.resolve({
          results: requests.map(() => ({
            hits: [],
            nbHits: 0,
            nbPages: 0,
            page: 0,
            processingTimeMS: 0,
            hitsPerPage: 0,
            exhaustiveNbHits: false,
            query: '',
            params: '',
          })),
        });
      }

      const excludeFromAnalytics = shouldExcludeCurrentUserFromAnalytics();
      const finalRequests = excludeFromAnalytics
        ? requests.map((r) => ({
            ...r,
            params: { ...r.params, analytics: false },
          }))
        : requests;

      return client.search(finalRequests);
    },
  };
}

export const searchClient = createSearchClient(algoliaClient);
export const gearMasterSearchClient = createSearchClient(gearMasterAlgoliaClient);
